import './styles/ManageMyJobs.scss';

import { Edit, Add } from '@mui/icons-material';
import { Grid, Paper, Divider, Box, CircularProgress } from '@mui/material';
import Alert from '@mui/material/Alert';

import parse from 'html-react-parser';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useJobmarketUniversityCompanyQuery } from 'api/hooks/jobmarket/companies';
import { useMyJobOfferingsQuery } from 'api/hooks/jobmarket/offers';
import { FormWrapperModal } from 'app/components/FormWrapperModal';
import { JobOfferItem } from 'app/components/RichList/items/JobOfferItem';
import { SubtleActionButton } from 'app/components/SubtleActionButton';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { FormErrorActions } from 'app/containers/Dashboard/FormError/slice';
import { translations } from 'locales/i18n';
import { Company, JobOffering } from 'types/AppTypes';
import { ACLHelper } from 'utils/ACLHelper';
import { countries } from 'utils/Countries';
import Roles from 'utils/enums/Roles';
import { useAuthContext } from 'utils/hooks/useAuthContext';
import { useCurrentURL } from 'utils/hooks/useCurrentURL';
import usePageHeading from 'utils/hooks/usePageHeading';

import { CompanyDetail } from '../CompanyDetail';
import { CompanyDetailContact } from '../CompanyDetail/components/CompanyDetailContact';
import { ManageOffer } from '../ManageOffer';
import NoOffer from './components/NoOffer';
import OfferWarningModal from './components/OfferWarning';

export default function ManageMyJobs() {
  const { t } = useTranslation();
  const [detailViewOpen, setDetailViewOpen] = useState(false);
  const [createOfferModalOpen, setCreateOfferModalOpen] = useState(false);
  const [createOfferModalWarningOpen, setCreateOfferModalWarningOpen] = useState(false);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentURL = useCurrentURL();
  const { user: currentUser } = useAuthContext();
  const aclHelper = new ACLHelper(currentUser);

  const { data: myJobOfferings, isLoading, isError } = useMyJobOfferingsQuery();
  const {
    data: universityCompany,
    isLoading: isUniversityCompanyLoading,
    isError: isUniversityCompanyError
  } = useJobmarketUniversityCompanyQuery();

  // TODO Muss überlegt werden ob das noch gebraucht wird, da es durch die Private Route schon geregelt sein sollte
  // if (!aclHelper.isCompanyUser()  && !aclHelper.isCompanyUser(id)) {
  //   dispatch(AlertActions.error(`${t(translations.pages.companyDetailPage.forbidden())}`));
  //   history.replace(`/companies/${userData.currentUser.company.id}`);
  // }

  usePageHeading(t(translations.layout.menu.manageMyJobs()));

  const shouldShowWarning = (companyData: Company) => {
    let emptyFields: string[] = [];
    if (
      !companyData.country ||
      countries.filter(country => country.label === companyData.country).length === 0
    ) {
      emptyFields.push(t(translations.pages.companyDetailPage.missingFields.country()));
    }
    if (!companyData.invoiceEmail) {
      emptyFields.push(t(translations.pages.companyDetailPage.missingFields.invoiceEmail()));
    }
    if (!companyData.taxId && !companyData.taxNumber) {
      emptyFields.push(t(translations.pages.companyDetailPage.missingFields.taxIdAndNumber()));
    }
    setMissingFields(emptyFields);
    if (emptyFields.length > 0) {
      return setCreateOfferModalWarningOpen(true);
    }
    return setCreateOfferModalOpen(true);
  };

  const userCanEdit = aclHelper.hasOne('entitlement', Roles.JOBMARKET_ADMIN);
  const userCanCreateJobOffers = aclHelper.isEmployee();
  const mainContactView =
    aclHelper.isEmployee() || aclHelper.hasOne('entitlement', Roles.JOBMARKET_ADMIN);
  const invoiceView =
    aclHelper.isCompanyUser() || aclHelper.hasOne('entitlement', Roles.JOBMARKET_ADMIN);

  const renderOffers = (jobOfferings: JobOffering[]) => {
    if (jobOfferings.length === 0) {
      return <NoOffer />;
    }

    if (aclHelper.isCompanyUser() || aclHelper.hasOne('entitlement', Roles.JOBMARKET_ADMIN)) {
      const openOffers = jobOfferings?.filter(o => o.isActive);
      const pastOffers = jobOfferings?.filter(o => !o.isActive);
      return (
        <Grid item xs>
          <Grid container>
            {openOffers.map(offer => (
              <Grid
                item
                xs={12}
                md={6}
                lg={4}
                className="joboffer"
                onClick={() => navigate(`/jobs/${offer.id}`, { state: { back: currentURL } })}
              >
                <JobOfferItem
                  key={offer.id}
                  jobOffer={offer}
                  withId={aclHelper.hasOne('entitlement', Roles.JOBMARKET_ADMIN)}
                  dense
                />
              </Grid>
            ))}
          </Grid>
          {openOffers.length > 0 && <Divider variant="fullWidth" />}
          <Grid container>
            {pastOffers.map(offer => (
              <Grid
                item
                xs={12}
                md={6}
                lg={4}
                className="joboffer"
                onClick={() => navigate(`/jobs/${offer.id}`, { state: { back: currentURL } })}
              >
                <JobOfferItem
                  key={offer.id}
                  jobOffer={offer}
                  withId={aclHelper.hasOne('entitlement', Roles.JOBMARKET_ADMIN)}
                  dense
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid container>
        {jobOfferings.map(offer => (
          <Grid
            item
            xs={12}
            md={6}
            lg={4}
            className="joboffer"
            onClick={() => navigate(`/jobs/${offer.id}`, { state: { back: currentURL } })}
          >
            <JobOfferItem
              key={offer.id}
              jobOffer={offer}
              withId={aclHelper.hasOne('entitlement', Roles.JOBMARKET_ADMIN)}
              dense
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  if (isError || isUniversityCompanyError) {
    dispatch(AlertActions.error(`${t(translations.pages.companyDetailPage.openFailed())}`));
    navigate('/companies', { replace: true });
    return;
  }

  if (isLoading || isUniversityCompanyLoading) {
    return <CircularProgress />;
  }

  const company = universityCompany as Company;
  const jobOfferings = myJobOfferings?.data as JobOffering[];

  if (company.state !== 'active' && !userCanEdit) {
    dispatch(AlertActions.error(`${t(translations.pages.companyDetailPage.notAvailable())}`));
    navigate('/', { replace: true });
  }

  return (
    <>
      <Helmet>
        <title>{company.name}</title>
      </Helmet>
      {userCanEdit && (
        <>
          <FormWrapperModal
            className="company-detail-modal"
            open={detailViewOpen}
            onClose={(_props, reason) => {
              if (reason && reason === 'backdropClick') return;
              setDetailViewOpen(false);
            }}
          >
            <div className="company-detail-modal__body">
              <CompanyDetail
                companyId={company.id}
                closeHandler={() => {
                  setDetailViewOpen(false);
                  dispatch(FormErrorActions.close());
                }}
                noAdmin
                onlyEdit
              />
            </div>
          </FormWrapperModal>
          <FormWrapperModal
            open={createOfferModalWarningOpen}
            onClose={() => {
              setCreateOfferModalWarningOpen(false);
            }}
          >
            <OfferWarningModal
              setOpen={setCreateOfferModalWarningOpen}
              isOpen={createOfferModalWarningOpen}
              setEditCompanyOpen={setDetailViewOpen}
              setCreateOfferOpen={setCreateOfferModalOpen}
              emptyFields={missingFields}
            />
          </FormWrapperModal>
          <FormWrapperModal
            className="job-offer-detail-modal"
            open={createOfferModalOpen}
            onClose={(_props, reason) => {
              if (reason && reason === 'backdropClick') return;
              setCreateOfferModalOpen(false);
            }}
          >
            <div className="job-offer-detail-modal__body">
              <ManageOffer
                company={company}
                newOffer
                closeHandler={() => setCreateOfferModalOpen(false)}
                isEmployee={aclHelper.isEmployee()}
              />
            </div>
          </FormWrapperModal>
        </>
      )}
      {userCanCreateJobOffers && (
        <>
          <FormWrapperModal
            open={createOfferModalWarningOpen}
            onClose={() => {
              setCreateOfferModalWarningOpen(false);
            }}
          >
            <OfferWarningModal
              setOpen={setCreateOfferModalWarningOpen}
              isOpen={createOfferModalWarningOpen}
              setEditCompanyOpen={setDetailViewOpen}
              setCreateOfferOpen={setCreateOfferModalOpen}
              emptyFields={missingFields}
            />
          </FormWrapperModal>
          <FormWrapperModal
            className="job-offer-detail-modal"
            open={createOfferModalOpen}
            onClose={(props, reason) => {
              if (reason && reason === 'backdropClick') return;
              setCreateOfferModalOpen(false);
            }}
          >
            <div className="job-offer-detail-modal__body">
              <ManageOffer
                company={company}
                newOffer
                closeHandler={() => setCreateOfferModalOpen(false)}
                isEmployee={aclHelper.isEmployee()}
              />
            </div>
          </FormWrapperModal>
        </>
      )}
      <Grid container className="company-detail-page">
        {company.state !== 'active' && userCanEdit && (
          <Grid item xs={12} className="company-detail-page__alert">
            <Alert severity="info">{t(translations.pages.companyDetailPage.notActive())}</Alert>
          </Grid>
        )}
        {userCanEdit && company.state === 'active' && (
          <Grid container justifyContent="flex-end" className="company-detail-page__controls">
            <SubtleActionButton type="default" icon={Edit} onClick={() => setDetailViewOpen(true)}>
              {t(translations.common.edit())}
            </SubtleActionButton>
          </Grid>
        )}
        <CompanyDetailContact
          company={company}
          mainContactView={mainContactView}
          invoiceView={invoiceView}
        />
        <Grid component={Paper} square item xs={12} md={9} className="company-detail-page__company">
          <Grid container className="company-detail-page__company__section">
            <Grid item xs className="company-detail-page__company__content">
              <Grid container className="company-detail-page__company__content__title">
                {company.name}
              </Grid>
              <div className="company-detail-page__company__content__description">
                <Box className="ql-editor">{parse(company.description)}</Box>
              </div>
            </Grid>
          </Grid>
          <Divider />
          <Grid container className="company-detail-page__company__section">
            <Grid
              container
              className="company-detail-page__company__section__header"
              justifyContent="space-between"
            >
              {t(translations.pages.jobOffers.jobOffers())} ({jobOfferings?.length}
              {jobOfferings?.length >= 50 ? '+' : ''})
              <Grid item xs="auto">
                {company.state === 'active' && (userCanEdit || userCanCreateJobOffers) && (
                  <Grid container justifyContent="flex-end">
                    <SubtleActionButton
                      type="primary"
                      icon={Add}
                      onClick={() => shouldShowWarning(company)}
                    >
                      {t(translations.pages.manageJobOffers.createNew())}
                    </SubtleActionButton>
                  </Grid>
                )}
              </Grid>
            </Grid>
            {renderOffers(jobOfferings)}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}
